import React, { useState, useEffect } from 'react';
import AdminCouponPanel from './AdminCouponPanel.jsx';

const AdminPanel = () => {
  // --- AUTH LAYER ---
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // VIEW CONSOLE LAYER SWITCH ('catalog', 'logistics', or 'coupons')
  const [activeTab, setActiveTab] = useState('catalog');
  const [adminOrders, setAdminOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const getAdminToken = () => {
    return localStorage.getItem('modische_token') || localStorage.getItem('token') || localStorage.getItem('modische_auth_token');
  };

  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch('https://api.modischekledings.com/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const data = await response.json();
      if (response.ok && data.user && (data.user.isAdmin || data.user.role === 'admin')) {
        setIsAuthenticated(true);
        localStorage.setItem('modische_token', data.token);
        
        localStorage.setItem('modische_user', JSON.stringify({
          name: data.user.name,
          email: data.user.email,
          isAdmin: true
        }));
        
        window.location.reload();
      } else {
        setLoginError('❌ Access Denied. Administrator clearance required.');
      }
    } catch (err) {
      setLoginError('❌ Cannot establish link to authorization server.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('modische_token');
    localStorage.removeItem('token');
    localStorage.removeItem('modische_user');
  };

  const initialFormState = {
    name: '', price: '', discountPrice: '', 
    description: 'Premium quality custom graphic tee.',
    details: '100% Luxury combed cotton. Heavyweight 240 GSM silhouette.', 
    category: 'Regular Fit', gender: 'Men', images: [''], sizes: []
  };

  const [product, setProduct] = useState(initialFormState);
  const [liveProducts, setLiveProducts] = useState([]);
  const [status, setStatus] = useState('');
  const [editingId, setEditingId] = useState(null); 

  const availableSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const fetchInventory = () => {
    fetch('https://api.modischekledings.com/api/products')
      .then(res => res.json())
      .then(data => setLiveProducts(Array.isArray(data) ? data : data.products || []))
      .catch(err => console.error("Error fetching inventory:", err));
  };

  const fetchAdminOrders = async (silent = false) => {
    if (!silent) setOrdersLoading(true);
    try {
      const response = await fetch('https://api.modischekledings.com/api/admin/orders', {
        headers: { 
          'Authorization': `Bearer ${getAdminToken()}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        setAdminOrders(data.orders || []);
      }
    } catch (err) {
      console.error("Fulfillment engine fetch break:", err);
    } finally {
      if (!silent) setOrdersLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (orderId, targetStatus) => {
    try {
      const response = await fetch(`https://api.modischekledings.com/api/admin/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify({ status: targetStatus })
      });
      if (response.ok) {
        alert(`Order record trace modified to: ${targetStatus}`);
        fetchAdminOrders();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // AUTO-LOGIN & DATA INITIALIZATION
  useEffect(() => {
    const checkActiveSession = () => {
      try {
        const token = getAdminToken();
        const targetUser = localStorage.getItem('modische_user');
        
        if (token && targetUser) {
          const parsed = JSON.parse(targetUser);
          if (parsed && (parsed.isAdmin === true || parsed.role === 'admin')) {
            setIsAuthenticated(true);
          }
        }
      } catch (e) {
        console.error("Admin auto-login bypass error:", e);
      }
    };

    checkActiveSession();
    
    if (isAuthenticated) {
      fetchInventory();
      fetchAdminOrders();
    }
  }, [isAuthenticated]);

  // REAL-TIME LOGISTICS AUTO-POLLING INTERVAL (8 SECONDS)
  useEffect(() => {
    let pollingTimer;

    if (isAuthenticated && activeTab === 'logistics') {
      pollingTimer = setInterval(() => {
        fetchAdminOrders(true);
      }, 8000);
    }

    return () => {
      if (pollingTimer) clearInterval(pollingTimer);
    };
  }, [isAuthenticated, activeTab]);

  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  const handleImageURLChange = (index, value) => {
    const updatedImages = [...product.images];
    updatedImages[index] = value;
    setProduct({ ...product, images: updatedImages });
  };

  const addImageField = () => {
    if (product.images.length < 5) {
      setProduct({ ...product, images: [...product.images, ''] });
    }
  };

  const removeImageField = (index) => {
    if (product.images.length > 1) {
      const updatedImages = product.images.filter((_, i) => i !== index);
      setProduct({ ...product, images: updatedImages });
    }
  };

  const handleSizeChange = (size) => {
    const updatedSizes = product.sizes.includes(size)
      ? product.sizes.filter(s => s !== size)
      : [...product.sizes, size];
    setProduct({ ...product, sizes: updatedSizes });
  };

  const startEdit = (item) => {
    setEditingId(item._id);
    setProduct({
      name: item.name, 
      price: item.price,
      discountPrice: item.discountPrice || '', 
      description: item.description || '',
      details: item.details || '', 
      category: item.category || 'Regular Fit', 
      gender: item.gender || 'Men',
      images: item.images && item.images.length > 0 ? item.images : [''],
      sizes: item.sizes || []
    });
    setActiveTab('catalog');
    setStatus(`✏️ Now editing: "${item.name}"`);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setProduct(initialFormState);
    setStatus('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(editingId ? 'Updating database entry...' : 'Uploading to Modische Database...');

    const cleanImages = product.images.filter(img => img.trim() !== '');

    const productPayload = {
      name: product.name, 
      price: Number(product.price), 
      discountPrice: product.discountPrice ? Number(product.discountPrice) : null, 
      description: product.description,
      details: product.details, 
      category: product.category, 
      gender: product.gender,
      sizes: product.sizes.length > 0 ? product.sizes : ['S', 'M', 'L', 'XL', 'XXL'],
      images: cleanImages
    };

    const url = editingId ? `https://api.modischekledings.com/api/products/${editingId}` : 'https://api.modischekledings.com/api/products';
    const method = editingId ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAdminToken()}`
        },
        body: JSON.stringify(productPayload)
      });

      if (response.ok) {
        setStatus(editingId ? '✨ Product updated!' : '✨ Product uploaded live!');
        setProduct(initialFormState);
        setEditingId(null);
        fetchInventory();
      } else {
        setStatus('❌ Operation failed. Validation check failed.');
      }
    } catch (err) {
      setStatus('❌ Network error. Could not reach server.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this item permanently?")) {
      try {
        const response = await fetch(`https://api.modischekledings.com/api/products/${id}`, { 
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAdminToken()}`
          }
        });
        if (response.ok) {
          setStatus('🗑️ Item deleted successfully!');
          if(editingId === id) cancelEdit();
          fetchInventory();
        } else {
          setStatus('❌ Failed to delete item.');
        }
      } catch (err) {
        setStatus('❌ Error connecting to backend server.');
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F5F5DC] flex items-center justify-center px-6">
        <div className="max-w-sm w-full bg-[#E5D3B3] p-8 rounded-sm shadow-xl text-center">
          <h2 className="text-xl font-serif text-[#2C1E12] mb-1 uppercase tracking-widest">Modische Portal</h2>
          <p className="text-[9px] text-[#664229] uppercase tracking-[0.2em] mb-6 opacity-70">Staff Account Authentication</p>
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <input type="email" name="email" placeholder="STAFF EMAIL ADDRESS" required value={loginData.email} onChange={handleLoginChange}
              className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs text-center tracking-wide focus:outline-none" />
            <input type="password" name="password" placeholder="ACCOUNT SECURE PASSWORD" required value={loginData.password} onChange={handleLoginChange}
              className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs text-center tracking-widest focus:outline-none" />
            <button type="submit" className="w-full py-3 bg-[#2C1E12] text-[#F5F5DC] text-[10px] uppercase tracking-widest font-bold hover:opacity-90 transition cursor-pointer border-none">
              Sign In to Management
            </button>
          </form>
          {loginError && <p className="mt-4 text-xs font-medium text-red-800 tracking-wide">{loginError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5DC] pt-36 pb-20 px-4 sm:px-8 max-w-[1500px] mx-auto text-left font-sans">
      
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-6 border-b border-[#664229]/20 mb-8 gap-4">
        <div>
          <h2 className="text-3xl font-serif uppercase tracking-wider text-[#2C1E12]">Master Control Panel</h2>
          <div className="flex flex-wrap items-center gap-4 mt-3">
            <button 
              onClick={() => { setActiveTab('catalog'); setStatus(''); }} 
              className={`text-[10px] uppercase tracking-widest font-black pb-1 border-b-2 transition bg-transparent border-none cursor-pointer ${activeTab === 'catalog' ? 'border-[#2C1E12] text-[#2C1E12]' : 'border-transparent text-zinc-400'}`}
            >
              🛠️ Catalog Designer
            </button>
            <button 
              onClick={() => { setActiveTab('logistics'); fetchAdminOrders(); }} 
              className={`text-[10px] uppercase tracking-widest font-black pb-1 border-b-2 transition bg-transparent border-none cursor-pointer ${activeTab === 'logistics' ? 'border-[#2C1E12] text-[#2C1E12]' : 'border-transparent text-zinc-400'}`}
            >
              📦 Logistics & Fulfillment ({adminOrders.length})
            </button>
            <button 
              onClick={() => { setActiveTab('coupons'); setStatus(''); }} 
              className={`text-[10px] uppercase tracking-widest font-black pb-1 border-b-2 transition bg-transparent border-none cursor-pointer ${activeTab === 'coupons' ? 'border-[#2C1E12] text-[#2C1E12]' : 'border-transparent text-zinc-400'}`}
            >
              🎟️ Campaign Coupons
            </button>
          </div>
        </div>
        <button type="button" onClick={handleLogout} className="text-[9px] font-bold text-red-800 uppercase tracking-wider border border-red-800/30 px-4 py-2 bg-white/40 hover:bg-white/80 transition rounded-xs cursor-pointer">
          Sign Out Portal
        </button>
      </div>

      {/* SECTOR 1: CATALOG */}
      {activeTab === 'catalog' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-[#E5D3B3] p-8 rounded-sm shadow-xl h-fit">
            <h3 className="text-xl font-serif text-[#2C1E12] mb-6 border-b border-[#664229]/10 pb-2">{editingId ? "Modify Existing Product" : "Add New Product"}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#664229] font-bold mb-2">Product Name</label>
                <input type="text" name="name" value={product.name} onChange={handleChange} required
                  className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs uppercase tracking-wider focus:outline-none" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#664229] font-bold mb-2">Original Price (₹)</label>
                  <input type="number" name="price" value={product.price} onChange={handleChange} required
                    className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs focus:outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-emerald-800 font-bold mb-2">Discount Price (₹)</label>
                  <input type="number" name="discountPrice" placeholder="No Discount" value={product.discountPrice} onChange={handleChange}
                    className="w-full p-3 bg-[#F5F5DC] text-emerald-800 font-bold text-xs focus:outline-none placeholder:text-zinc-400" />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#664229] font-bold">Product Image URLs ({product.images.length}/5)</label>
                  {product.images.length < 5 && (
                    <button type="button" onClick={addImageField} className="text-[9px] uppercase tracking-wider font-bold text-[#664229] border-b border-[#664229] bg-transparent cursor-pointer">+ Add URL Link</button>
                  )}
                </div>
                <div className="space-y-3">
                  {product.images.map((url, index) => (
                    <div key={index} className="flex gap-2 items-center">
                      <input type="url" value={url} onChange={(e) => handleImageURLChange(index, e.target.value)} required={index === 0} placeholder={`Image URL # ${index + 1}`}
                        className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs focus:outline-none" />
                      {product.images.length > 1 && <button type="button" onClick={() => removeImageField(index)} className="text-xs text-red-700 font-bold px-1 bg-transparent border-none cursor-pointer">✕</button>}
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-[#664229]/20 pt-4 space-y-4">
                <h4 className="text-[11px] uppercase tracking-[0.2em] text-[#2C1E12] font-black">Content & Text Management</h4>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Heading: Description</label>
                  <textarea name="description" value={product.description} onChange={handleChange} required rows="3"
                    className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs focus:outline-none font-serif resize-none" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Heading: Details</label>
                  <textarea name="details" value={product.details} onChange={handleChange} required rows="3"
                    className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs focus:outline-none font-serif resize-none" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#664229] font-bold mb-2">Gender</label>
                  <select name="gender" value={product.gender} onChange={handleChange} className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs focus:outline-none">
                    <option value="Men">Men</option> <option value="Women">Women</option> <option value="Unisex">Unisex</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#664229] font-bold mb-2">Fit Style</label>
                  <select name="category" value={product.category} onChange={handleChange} className="w-full p-3 bg-[#F5F5DC] text-[#2C1E12] text-xs focus:outline-none">
                    <option value="Regular Fit">Regular Fit</option> <option value="Oversized">Oversized</option> <option value="Accessories">Accessories</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-[#664229] font-bold mb-2">Available Sizes</label>
                <div className="flex gap-2 flex-wrap">
                  {availableSizes.map(size => (
                    <button type="button" key={size} onClick={() => handleSizeChange(size)}
                      className={`px-3 py-2 text-xs font-mono border transition cursor-pointer ${product.sizes.includes(size) ? 'bg-[#2C1E12] text-[#F5F5DC] border-transparent' : 'border-[#664229]/30 text-[#2C1E12]'}`}>{size}</button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-4">
                <button type="submit" className="flex-1 py-4 bg-[#2C1E12] text-[#F5F5DC] text-[10px] uppercase tracking-[0.3em] font-bold hover:opacity-90 transition border-none cursor-pointer">
                  {editingId ? "Save Changes" : "Upload Live"}
                </button>
                {editingId && <button type="button" onClick={cancelEdit} className="px-6 py-4 bg-zinc-600 text-white text-[10px] uppercase tracking-widest font-bold hover:bg-zinc-700 transition border-none cursor-pointer">Cancel</button>}
              </div>
            </form>
            {status && <p className="mt-6 text-center text-xs font-medium text-[#664229] tracking-wide">{status}</p>}
          </div>

          <div className="bg-[#E5D3B3] p-8 rounded-sm shadow-xl h-fit max-h-[700px] overflow-y-auto">
            <h2 className="text-2xl font-serif text-[#2C1E12] mb-6 border-b border-[#664229]/20 pb-4">Current Inventory</h2>
            <div className="space-y-4">
              {liveProducts.map(item => (
                <div key={item._id} className="flex justify-between items-center bg-[#F5F5DC] p-4 rounded-sm border border-[#664229]/10">
                  <div className="flex items-center gap-3">
                    {item.images?.[0] && <img src={item.images[0]} alt="" className="w-10 h-12 object-cover rounded-sm bg-[#E5D3B3]" />}
                    <div className="text-left">
                      <h4 className="text-xs font-bold text-[#2C1E12] uppercase tracking-wider max-w-[150px] truncate">{item.name}</h4>
                      <p className="text-[10px] text-[#664229]">₹{item.discountPrice || item.price}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(item)} className="text-[10px] uppercase tracking-widest font-bold text-[#664229] border border-[#664229]/30 px-3 py-2 transition hover:bg-[#2C1E12] hover:text-[#F5F5DC] bg-transparent cursor-pointer">Edit</button>
                    <button onClick={() => handleDelete(item._id)} className="text-[10px] uppercase tracking-widest font-bold text-red-700 border border-red-700/20 px-3 py-2 transition bg-white/50 cursor-pointer">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* SECTOR 2: LOGISTICS & SHIPMENT FULFILLMENT */}
      {activeTab === 'logistics' && (
        <div className="bg-[#E5D3B3] p-4 sm:p-8 rounded-sm shadow-xl min-h-[500px] w-full max-w-full block overflow-hidden">
          <div className="flex justify-between items-center mb-6 border-b border-[#664229]/10 pb-4">
            <h3 className="text-xl font-serif text-[#2C1E12] uppercase tracking-wider font-bold">Active Sales & Dispatch Register</h3>
            <span className="text-[10px] font-mono font-bold text-emerald-800 bg-emerald-100/80 border border-emerald-300 px-3 py-1 rounded-full animate-pulse">
              🟢 Live Stream Sync
            </span>
          </div>
          
          {ordersLoading ? (
            <div className="text-center py-12 font-mono text-xs uppercase tracking-widest text-[#664229]">Syncing logistics streams...</div>
          ) : adminOrders.length === 0 ? (
            <div className="text-center py-12 text-xs font-mono text-zinc-500 uppercase tracking-wider">No customer transactions log profiles found inside cloud index.</div>
          ) : (
            <div className="w-full max-w-full inline-block overflow-x-auto scrollbar-thin rounded-xs border border-[#2C1E12]/10 bg-[#FAF9F6]/20">
              <table className="w-full text-left border-collapse min-w-[950px] table-fixed">
                <thead>
                  <tr className="bg-[#2C1E12] text-[#F5F5DC] text-[9px] uppercase tracking-widest font-mono">
                    <th className="p-4 border-none w-[110px]">Receipt ID</th>
                    <th className="p-4 border-none w-[90px]">Date Log</th>
                    <th className="p-4 border-none w-[380px]">Client Parameters & Packing List</th>
                    <th className="p-4 border-none w-[90px]">Total Bill</th>
                    <th className="p-4 border-none w-[110px]">Logistics Track</th>
                    <th className="p-4 border-none text-right w-[170px] pr-4">Fulfillment Handle</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#664229]/10 text-xs text-zinc-800 font-sans">
                  {adminOrders.map((order) => {
                    const awbCode = order.shiprocketAwb || order.trackingNumber || (order.returnReason?.includes('AWB:') ? order.returnReason.split('AWB:')[1]?.split('|')[0]?.trim() : null);

                    return (
                      <tr key={order._id} className="hover:bg-white/30 transition align-top">
                        <td className="p-4 font-mono font-bold text-zinc-600 select-all cursor-pointer" title="Click to copy full ID">
                          <div className="text-[10px] text-[#2C1E12] bg-white/60 p-1.5 rounded-xs border border-[#664229]/20 break-all max-w-[120px]">
                            {order._id}
                          </div>
                          {order.orderId && (
                            <div className="text-[8px] text-zinc-500 mt-1 truncate">
                              Ref: {order.orderId}
                            </div>
                          )}
                        </td>
                        <td className="p-4 font-mono text-zinc-500 text-[10px]">{new Date(order.createdAt).toLocaleDateString()}</td>
                        
                        <td className="p-4 whitespace-normal break-words">
                          <div className="mb-3 max-w-full">
                            <p className="font-bold text-[#2C1E12] uppercase tracking-wide">{order.user?.name || order.shippingAddress?.fullName || 'Verified Client'}</p>
                            <p className="text-[10px] text-zinc-500 font-mono mb-1">{order.user?.email || order.shippingAddress?.email || 'N/A'}</p>
                            <p className="text-[10px] text-zinc-700 bg-white/50 p-2 rounded-xs border border-[#664229]/10 whitespace-normal break-words max-w-full">
                              📍 {order.shippingAddress?.street || order.shippingAddress?.address}, {order.shippingAddress?.city}, {order.shippingAddress?.state} - <strong>{order.shippingAddress?.pincode}</strong> (Ph: {order.shippingAddress?.phone})
                            </p>
                          </div>

                          <div className="bg-white/80 border border-[#664229]/10 rounded-xs p-3 space-y-2.5 shadow-xs max-w-full">
                            {order.items && order.items.map((item, idx) => {
                              const productInfo = item.product || {};
                              const displayImage = productInfo.image || (Array.isArray(productInfo.images) ? productInfo.images[0] : null);

                              return (
                                <div key={idx} className="flex items-center gap-3 pb-2 last:pb-0 border-b border-zinc-200/50 last:border-none w-full min-w-0">
                                  <div className="w-10 h-12 bg-zinc-100 border border-zinc-200 rounded-xs overflow-hidden flex-shrink-0">
                                    <img 
                                      src={displayImage || 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=150'} 
                                      alt="" 
                                      className="w-full h-full object-cover"
                                      onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=150'; }}
                                    />
                                  </div>
                                  <div className="flex-1 min-w-0 text-left">
                                    <h5 className="text-[11px] font-serif font-black uppercase text-[#2C1E12] truncate">
                                      {item.name || productInfo.name || "Premium Garment"}
                                    </h5>
                                    <div className="flex items-center gap-3 text-[9px] font-mono font-bold text-zinc-500 uppercase mt-0.5">
                                      <div>SIZE: <span className="text-red-950 bg-red-50 px-1 rounded-xs">{item.size || 'M'}</span></div>
                                      <div>QTY: <span className="text-[#2C1E12]">{item.quantity || 1}</span></div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </td>

                        <td className="p-4 font-serif font-black text-[#2C1E12]">₹{order.totalAmount}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 text-[8px] font-mono font-black uppercase tracking-widest border rounded-xs ${
                            order.status === 'Delivered' ? 'border-green-600 text-green-700 bg-green-50' :
                            order.status.includes('Requested') ? 'border-amber-600 text-amber-700 bg-amber-50' :
                            'border-[#2C1E12]/40 text-[#2C1E12] bg-[#F5F5DC]'
                          }`}>{order.status}</span>

                          {awbCode && (
                            <div className="mt-2 text-[8px] font-mono text-zinc-500">
                              AWB:{' '}
                              <a 
                                href={`https://shiprocket.co/tracking/${awbCode}`} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-bold underline text-red-950"
                              >
                                {awbCode}
                              </a>
                            </div>
                          )}
                        </td>
                        
                        <td className="p-4 text-right pr-4">
                          {order.status === 'Processing' && (
                            <button onClick={() => handleUpdateOrderStatus(order._id, 'Shipped')} className="text-[9px] font-black font-mono uppercase tracking-widest bg-blue-700 text-white px-3 py-2 hover:bg-blue-800 transition rounded-sm cursor-pointer border-none shadow-sm whitespace-nowrap">📦 Dispatch Item</button>
                          )}
                          {order.status === 'Shipped' && (
                            <button onClick={() => handleUpdateOrderStatus(order._id, 'Delivered')} className="text-[9px] font-black font-mono uppercase tracking-widest bg-green-700 text-white px-3 py-2 hover:bg-green-800 transition rounded-sm cursor-pointer border-none shadow-sm whitespace-nowrap">✅ Confirm Delivery</button>
                          )}
                          
                          {order.status.includes('Requested') && (
                            <div className="text-[9px] font-mono font-bold text-amber-800 bg-amber-50/80 border border-amber-200 p-3 rounded-sm text-left max-w-xs whitespace-normal space-y-2 shadow-xs">
                              <div>
                                <span className="text-[8px] uppercase tracking-wider bg-amber-200 text-amber-900 font-black px-1.5 py-0.5 rounded-xs">Claim Meta DataLog</span>
                                <p className="mt-1 text-[11px] font-sans font-medium text-zinc-800 whitespace-normal break-words">{order.returnReason || 'No descriptive context filed.'}</p>
                              </div>

                              {order.returnImages && order.returnImages.length > 0 && (
                                <div>
                                  <span className="text-[8px] uppercase tracking-wider text-zinc-400 block mb-1">Uploaded Inspection Images ({order.returnImages.length}/6)</span>
                                  <div className="grid grid-cols-3 gap-1">
                                    {order.returnImages.map((src, i) => (
                                      <a href={src} target="_blank" rel="noopener noreferrer" key={i} className="aspect-[3/4] border border-zinc-200 bg-white rounded-xs overflow-hidden block hover:opacity-80 transition shadow-xs" title="Click to pop-out full size audit asset image">
                                        <img src={src} alt="" className="w-full h-full object-cover" />
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* SECTOR 3: LIVE PROMO COUPONS */}
      {activeTab === 'coupons' && (
        <div className="w-full animate-fade-in">
          <AdminCouponPanel />
        </div>
      )}

    </div>
  );
};

export default AdminPanel;